/**
 * Frontend mirror of /app/backend/services/rbac_matrix.py.
 *
 * This file lets the route guards make synchronous decisions without an
 * extra API round-trip on every navigation. The admin Permissions page still
 * fetches the canonical matrix from `/api/rbac/matrix` so any drift between
 * the two files is immediately visible to administrators.
 *
 * IMPORTANT: keep this in sync with the backend file.
 */

export const ROLES = [
  { id: 'administrator',   label: 'Administrator' },
  { id: 'project_manager', label: 'Project Manager' },
  { id: 'investor',        label: 'Investor' },
  { id: 'social_actor',    label: 'Social Actor' },
  { id: 'guest',           label: 'Guest' },
];

const ALL_ROLES = ROLES.map((r) => r.id);
const NON_GUEST = ALL_ROLES.filter((r) => r !== 'guest');
const ADMIN_ONLY = ['administrator'];
const ADMIN_PM = ['administrator', 'project_manager'];

// Quick-access constants for App.js
export const RBAC = {
  ALL_ROLES,
  NON_GUEST,
  ADMIN_ONLY,
  ADMIN_PM,
};

export const MODULE_PATHS = {
  // Core
  '/dashboard':                       ALL_ROLES,
  '/guides':                          ALL_ROLES,

  // Registries (no guest)
  '/projects':                        NON_GUEST,
  '/templates':                       NON_GUEST,
  '/social-actor-registry':           NON_GUEST,
  '/social-investors':                NON_GUEST,

  // Operational (open to all incl. guest)
  '/social-investment-registration':  ALL_ROLES,
  '/participatory-planning':          ALL_ROLES,
  '/capacity-building':               ALL_ROLES,

  // Operational (no guest)
  '/combined-investments':            NON_GUEST,
  '/supply-demand-matching':          NON_GUEST,
  '/investment-evaluation':           NON_GUEST,
  '/impact-dashboard':                NON_GUEST,
  '/qa-overview':                     NON_GUEST,
  '/certification-renewal':           NON_GUEST,
  '/continuous-improvement':          ADMIN_PM,

  // Administration
  '/admin/users':                     ADMIN_ONLY,
  '/admin/reports':                   ADMIN_ONLY,
  '/admin/settings':                  ADMIN_ONLY,
  '/admin/contact-requests':          ADMIN_ONLY,
  '/admin/pending-registrations':     ADMIN_ONLY,
  '/admin/permissions':               ADMIN_ONLY,
};

/** Returns true if the given user (or null) is allowed on the given role list. */
export function hasAccess(user, allowedRoles) {
  if (!user) return false;
  if (!allowedRoles || allowedRoles.length === 0) return true;
  // Map alias 'admin' → 'administrator'
  const role = user.role === 'admin' ? 'administrator' : user.role;
  return allowedRoles.includes(role);
}
